// Iter AX — first-login welcome + spotlight tour. No external tour library:
// a fixed highlight box (huge box-shadow cutout) + a positioned tooltip card.
import { useEffect, useMemo, useState } from "react";
import { Sparkles } from "lucide-react";

const STEPS = [
  { target: null, title: "Welcome to Posturra 👋", body: "Your treatment room copilot — posture photos, AI analysis, treatment mapping, SOAP notes, and outcome tracking, all in one clinical flow. Here's a 60-second tour." },
  { target: '[data-testid="add-client-btn"]', title: "1 · Add your first client", body: "Everything starts here. Create a client, then start a session — the app walks you through the same 4 phases as a real appointment." },
  { target: '[data-testid="client-search-input"]', title: "2 · Find anyone fast", body: "Search by name, phone, or email. Clients sort alphabetically, and Active / Archived / All filters keep the list clean." },
  { target: '[data-testid="nav-insights"]', title: "3 · Track outcomes", body: "Insights shows pain improvements, session trends, and progress across your whole practice." },
  { target: '[data-testid="nav-settings"]', title: "4 · Make it yours", body: "Settings holds your clinic details, biometric sign-in, the documentation grace period, and tester invites." },
  { target: '[data-testid="lock-app-btn"]', title: "5 · One-tap privacy", body: "Tap the lock anytime to instantly hide all client information. The app also locks itself when a treatment window ends." },
  { target: null, title: "You're all set!", body: "The Getting Started checklist on your dashboard will track your first steps. You can replay this tour from there anytime." },
];

export const OnboardingTour = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);

  useEffect(() => { if (open) setStep(0); }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const measure = () => {
      const sel = STEPS[step].target;
      const el = sel ? document.querySelector(sel) : null;
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0) { setRect({ top: r.top, left: r.left, width: r.width, height: r.height }); return; }
      }
      setRect(null);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open, step]);

  const tooltipStyle = useMemo(() => {
    if (!rect) return null;
    const below = rect.top + rect.height + 220 < window.innerHeight;
    return {
      top: below ? rect.top + rect.height + 14 : undefined,
      bottom: below ? undefined : window.innerHeight - rect.top + 14,
      left: Math.max(16, Math.min(rect.left, window.innerWidth - 376)),
    };
  }, [rect]);

  if (!open) return null;
  const s = STEPS[step];
  const last = step === STEPS.length - 1;

  const card = (
    <div className="bg-white rounded-3xl border border-paper-200 shadow-2xl p-6 w-[360px] max-w-[calc(100vw-32px)]" data-testid={`tour-step-${step}`}>
      {step === 0 && (
        <span className="w-10 h-10 rounded-xl bg-sage-100 text-sage-700 inline-flex items-center justify-center mb-3">
          <Sparkles className="w-5 h-5" strokeWidth={1.5} />
        </span>
      )}
      <div className="font-heading text-lg text-ink-900 mb-1.5">{s.title}</div>
      <p className="text-sm text-ink-700 leading-relaxed mb-5">{s.body}</p>
      <div className="flex items-center justify-between gap-3">
        <button data-testid="tour-skip-btn" onClick={() => onClose(false)} className="text-xs text-ink-500 hover:text-ink-900 underline underline-offset-4">
          Skip tour
        </button>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 mr-1">
            {STEPS.map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === step ? "bg-sage-500" : "bg-paper-200"}`} />
            ))}
          </div>
          {step > 0 && (
            <button data-testid="tour-back-btn" onClick={() => setStep(step - 1)} className="min-h-[40px] px-4 rounded-full border border-paper-200 hover:bg-paper-100 text-ink-700 text-sm">
              Back
            </button>
          )}
          <button
            data-testid="tour-next-btn"
            onClick={() => (last ? onClose(true) : setStep(step + 1))}
            className="min-h-[40px] px-5 rounded-full bg-sage-500 hover:bg-sage-600 text-white text-sm font-medium"
          >
            {last ? "Finish" : step === 0 ? "Show me around" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[80]" data-testid="onboarding-tour">
      {rect ? (
        <>
          <div
            className="fixed rounded-2xl transition-all duration-300 pointer-events-none"
            style={{
              top: rect.top - 8, left: rect.left - 8,
              width: rect.width + 16, height: rect.height + 16,
              boxShadow: "0 0 0 9999px rgba(28,25,23,0.62)",
              border: "2px solid rgba(134,161,132,0.9)",
            }}
          />
          <div className="fixed z-[81]" style={tooltipStyle}>{card}</div>
        </>
      ) : (
        <div className="fixed inset-0 bg-ink-900/60 backdrop-blur-sm flex items-center justify-center px-4">{card}</div>
      )}
    </div>
  );
};
